import React, { useEffect, useState, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { FileText, FileImage, FileSpreadsheet, FileVideo, Download, X, Loader, AlertCircle, File } from 'lucide-react';
import Modal from './Modal';
import { notifyToast } from '../../lib/toastStore';

interface DocPreviewModalProps {
  open: boolean;
  onClose: () => void;
  storagePath: string;
  fileName: string;
  mimeType?: string;
}

type PreviewType = 'pdf' | 'image' | 'video' | 'docx' | 'xlsx' | 'unsupported';

function detectType(fileName: string, mimeType?: string): PreviewType {
  const lower = fileName.toLowerCase();
  if (/\.pdf$/i.test(lower)) return 'pdf';
  if (/\.(png|jpg|jpeg|gif|webp|bmp|svg)$/i.test(lower)) return 'image';
  if (/\.(mp4|webm|mov|avi|mkv|wmv|flv|ogv)$/i.test(lower)) return 'video';
  if (/\.docx?$/i.test(lower)) return 'docx';
  if (/\.xlsx?$/i.test(lower)) return 'xlsx';
  // Fall back to mime type
  const mime = (mimeType || '').toLowerCase();
  if (mime.includes('pdf')) return 'pdf';
  if (mime.includes('image')) return 'image';
  if (mime.includes('video')) return 'video';
  if (mime.includes('word') || mime.includes('document')) return 'docx';
  if (mime.includes('spreadsheet') || mime.includes('excel')) return 'xlsx';
  return 'unsupported';
}

function fileIcon(type: PreviewType) {
  switch (type) {
    case 'pdf': return <FileText className="h-5 w-5 text-red-400" />;
    case 'image': return <FileImage className="h-5 w-5 text-blue-400" />;
    case 'video': return <FileVideo className="h-5 w-5 text-purple-400" />;
    case 'docx': return <FileText className="h-5 w-5 text-indigo-400" />;
    case 'xlsx': return <FileSpreadsheet className="h-5 w-5 text-emerald-400" />;
    default: return <File className="h-5 w-5 text-slate-400" />;
  }
}

export default function DocPreviewModal({ open, onClose, storagePath, fileName, mimeType }: DocPreviewModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const [xlsxData, setXlsxData] = useState<{ sheetName: string; rows: any[][] } | null>(null);
  const previewType = detectType(fileName, mimeType);

  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      return await getAuth().currentUser?.getIdToken() || null;
    } catch {
      return null;
    }
  }, []);

  const [serveBlobUrl, setServeBlobUrl] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);

  // Fetch a file from /api/serve-inline with auth header and return a blob URL
  const fetchServeBlob = useCallback(async (token: string): Promise<string | null> => {
    if (!storagePath || storagePath === 'undefined' || storagePath === 'null') {
      return null;
    }
    try {
      const res = await fetch(`/api/serve-inline?path=${encodeURIComponent(storagePath)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }, [storagePath]);

  // Reset state whenever the modal opens with a new document
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setDocxHtml(null);
    setXlsxData(null);
    setServeBlobUrl(null);
    setAuthToken(null);

    const load = async () => {
      if (!storagePath || storagePath === 'undefined' || storagePath === 'null') {
        setError('Document path or reference is missing.');
        setLoading(false);
        return;
      }

      const token = await getToken();
      if (!token) { setError('Authentication failed'); setLoading(false); return; }
      setAuthToken(token);

      if (previewType === 'pdf') {
        // For PDFs, fetch as blob and create a local blob URL for the iframe
        // (iframe with blob URLs works in Chrome's PDF viewer)
        const blobUrl = await fetchServeBlob(token);
        if (blobUrl) {
          setServeBlobUrl(blobUrl);
        } else {
          setError('Failed to load PDF');
        }
        setLoading(false);
      } else if (previewType === 'image' || previewType === 'video') {
        const blobUrl = await fetchServeBlob(token);
        if (blobUrl) {
          setServeBlobUrl(blobUrl);
        } else {
          setError('Failed to load file');
        }
        setLoading(false);
      } else if (previewType === 'docx') {
        try {
          const res = await fetch(`/api/preview/docx?storagePath=${encodeURIComponent(storagePath)}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
            throw new Error(err.error || `HTTP ${res.status}`);
          }
          const data = await res.json();
          setDocxHtml(data.html || '');
        } catch (e: any) {
          setError(e.message);
        }
        setLoading(false);
      } else if (previewType === 'xlsx') {
        try {
          const res = await fetch(`/api/preview/xlsx?storagePath=${encodeURIComponent(storagePath)}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
            throw new Error(err.error || `HTTP ${res.status}`);
          }
          const data = await res.json();
          setXlsxData({ sheetName: data.sheetName, rows: data.rows });
        } catch (e: any) {
          setError(e.message);
        }
        setLoading(false);
      } else {
        setLoading(false);
      }
    };

    load();

    // Clean up blob URLs on unmount or re-open
    return () => {
      if (serveBlobUrl) URL.revokeObjectURL(serveBlobUrl);
    };
  }, [open, storagePath, previewType, getToken, fetchServeBlob]);

  const handleDownload = async () => {
    if (!storagePath || storagePath === 'undefined' || storagePath === 'null') {
      notifyToast('warning', 'Document storage reference is missing or unavailable.');
      return;
    }
    const token = await getToken();
    if (!token) return;
    try {
      const res = await fetch(`/api/serve-inline?path=${encodeURIComponent(storagePath)}&download=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Fallback: open in new tab if path is valid
      if (storagePath && storagePath !== 'undefined' && storagePath !== 'null') {
        window.open(`/api/serve-inline?path=${encodeURIComponent(storagePath)}&download=true`, '_blank');
      }
    }
  };

  const [pdfLoaded, setPdfLoaded] = useState(false);

  if (!open) return null;

  return (
    <Modal onClose={onClose} backdropClassName="bg-black/72 backdrop-blur-xl">
      <div className="bg-slate-900 border border-white/20 rounded-2xl shadow-2xl w-full max-w-6xl h-[90dvh] max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden"
           style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.6)' }}>
        {/* ── Header bar ─────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-3 shrink-0 border-b border-white/10">
          <div className="flex items-center gap-2.5 min-w-0">
            {fileIcon(previewType)}
            <span className="text-sm font-semibold text-white truncate">{fileName}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 text-caption font-semibold text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
              title="Download"
            >
              <Download className="h-3.5 w-3.5" />
              <span>Download</span>
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ── Content area ───────────────────────────────────── */}
        <div className="flex-1 min-h-0 relative bg-slate-800/50">
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <Loader className="h-8 w-8 text-white/50 animate-spin" />
              <p className="text-sm text-white/60 font-medium">Loading preview...</p>
            </div>
          )}

          {error && !loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8">
              <AlertCircle className="h-10 w-10 text-red-400/70" />
              <p className="text-sm text-red-300/80 font-medium text-center max-w-md">{error}</p>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-xl text-sm font-semibold transition-all cursor-pointer"
              >
                <Download className="h-4 w-4" />
                <span>Download instead</span>
              </button>
            </div>
          )}

          {!loading && !error && previewType === 'pdf' && (
            <>
              {!pdfLoaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                  <div className="w-12 h-12 rounded-full border-2 border-white/20 border-t-white/60 animate-spin" />
                  <p className="text-sm text-white/60 font-medium">Loading PDF preview...</p>
                </div>
              )}
              <iframe
                src={serveBlobUrl || ''}
                className={`absolute inset-0 w-full h-full border-0 ${pdfLoaded ? '' : 'invisible'}`}
                title={fileName}
                onLoad={() => setPdfLoaded(true)}
              />
            </>
          )}

          {!loading && !error && previewType === 'image' && (
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <img
                src={serveBlobUrl || ''}
                alt={fileName}
                className="max-w-full max-h-full rounded-lg shadow-lg object-contain"
              />
            </div>
          )}

          {!loading && !error && previewType === 'video' && (
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <video
                src={serveBlobUrl || ''}
                controls
                className="max-w-full max-h-full rounded-lg shadow-lg"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
              >
                Your browser does not support video playback.
              </video>
            </div>
          )}

          {!loading && !error && previewType === 'docx' && docxHtml && (
            <iframe
              srcDoc={`<html><head><meta charset="utf-8"><style>body{font-family:system-ui,-apple-system,sans-serif;padding:32px;color:#111;line-height:1.6;max-width:800px;margin:0 auto;}img{max-width:100%;height:auto;}table{border-collapse:collapse;width:100%;}td,th{border:1px solid #ddd;padding:8px;}th{background:#f5f5f5;}</style></head><body>${docxHtml}</body></html>`}
              sandbox="allow-same-origin"
              className="absolute inset-0 w-full h-full border-0"
              style={{ background: '#fff' }}
              title={fileName}
            />
          )}

          {!loading && !error && previewType === 'xlsx' && xlsxData && (
            <div className="absolute inset-0 overflow-auto p-4">
              <div className="flex items-center gap-3 mb-2">
                {xlsxData.sheetName && (
                  <span className="text-caption font-semibold text-white/60">
                    Sheet: {xlsxData.sheetName}
                  </span>
                )}
                <span className="text-caption text-white/40">
                  {xlsxData.rows.length > 1 ? `${xlsxData.rows.length - 1} data rows` : `${xlsxData.rows.length} rows`}
                  {xlsxData.rows[0] ? ` · ${xlsxData.rows[0].length} columns` : ''}
                </span>
                {xlsxData.rows.length >= 500 && (
                  <span className="text-caption text-amber-400/70">(first 500 rows shown)</span>
                )}
              </div>
              <div className="overflow-auto rounded-lg border border-white/10 bg-white">
                <table className="w-full text-sm">
                  {xlsxData.rows.length > 0 && (
                    <thead>
                      <tr>
                        {(xlsxData.rows[0] || []).map((cell: any, ci: number) => (
                          <th
                            key={ci}
                            className="sticky top-0 px-3 py-2 text-left font-bold text-slate-700 bg-slate-50 border-b border-slate-200 whitespace-nowrap"
                          >
                            {cell != null ? String(cell) : ''}
                          </th>
                        ))}
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {xlsxData.rows.slice(1).map((row: any[], ri: number) => (
                      <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                        {(row || []).map((cell: any, ci: number) => (
                          <td key={ci} className="px-3 py-1.5 text-slate-600 border-b border-slate-100 whitespace-nowrap">
                            {cell != null ? String(cell) : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!loading && !error && previewType === 'unsupported' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8">
              <File className="h-12 w-12 text-white/30" />
              <p className="text-sm text-white/50 font-medium">Preview not available for this file type.</p>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-xl text-sm font-semibold transition-all cursor-pointer"
              >
                <Download className="h-4 w-4" />
                <span>Download file</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
